import { useEffect } from 'react';
import { useSendWhatsAppFreeForm } from './useSendWhatsAppFreeForm';

/* ------------------------------------------------------------------ */
/*  useWhatsAppSharePreflight (PRD.235/238/239/240)                   */
/*                                                                    */
/*  Shared 24h-window preflight for the WhatsApp share modals         */
/*  (prescription / invoice / comparison). Reuses the free-form       */
/*  endpoint's `dryRun` eligibility check — the opt-in + 24h window   */
/*  rule is per-patient and identical across share types — so the     */
/*  modal can show the window state (and flip Send → "Send anyway")   */
/*  the moment it opens, instead of after a failed first send.        */
/*                                                                    */
/*  Degrades gracefully: if the free-form endpoint isn't configured   */
/*  for this client the preflight is skipped and the modal falls back */
/*  to discovering the closed window from the send response.          */
/* ------------------------------------------------------------------ */

export interface WhatsAppSharePreflight {
  loading: boolean;
  /** Window closed but forceable via "Send anyway" (no_inbound / window_expired). */
  windowClosed: boolean;
  /** Opt-in / opt-out block — a compliance gate that force cannot bypass. */
  optInBlocked: boolean;
  /** A short warning to render in the modal, or null when the window is open/unknown. */
  warning: string | null;
}

export function useWhatsAppSharePreflight(
  open: boolean,
  patientId: string,
): WhatsAppSharePreflight {
  const { eligibility, checkEligibility, enabled } = useSendWhatsAppFreeForm();

  useEffect(() => {
    if (open && enabled && patientId) void checkEligibility(patientId);
  }, [open, enabled, patientId, checkEligibility]);

  const blocked = eligibility.allowed === false;
  const windowClosed =
    blocked && (eligibility.reason === 'no_inbound' || eligibility.reason === 'window_expired');
  const optInBlocked =
    blocked && (eligibility.reason === 'opted_out' || eligibility.reason === 'opt_in_required');

  const warning = windowClosed
    ? 'This patient is outside the WhatsApp 24-hour window. “Send anyway” will only deliver if the window is actually open.'
    : optInBlocked
      ? (eligibility.message ?? 'This patient has not opted in to WhatsApp messages.')
      : null;

  return { loading: eligibility.loading, windowClosed, optInBlocked, warning };
}
